import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { TrendingUp, Users, X, Shield, CheckCircle2, ArrowRight, Zap } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (user) navigate('/dashboard');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const benefits = [
    {
      icon: <TrendingUp size={22} />,
      color: 'text-secondary',
      bg: 'bg-secondary/10',
      title: 'Pagamento apenas por resultado',
      description: 'Sem taxas de configuração, sem subscrições. Pague apenas quando receber um pedido de contacto.'
    },
    {
      icon: <Users size={22} />,
      color: 'text-purple-500',
      bg: 'bg-purple-500/10',
      title: 'Pedidos de contacto pré-qualificados',
      description: 'Cada pedido de contacto é filtrado e qualificado antes de chegar até si.'
    },
    {
      icon: <X size={22} />,
      color: 'text-orange-500',
      bg: 'bg-orange-500/10',
      title: 'Cancele quando quiser',
      description: 'Sem contratos, sem compromissos. Pause ou cancele a sua conta com um clique.'
    }
  ];

  const heroChips = [
    'Primeiro lead grátis',
    'Sem subscrições',
    'Cancele quando quiser',
  ];

  return (
    <div className="min-h-screen bg-background font-sans">

      {/* ── HEADER ─────────────────────────────────────────────────── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background/90 backdrop-blur-md shadow-sm border-b border-primary/10'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 h-16 flex justify-between items-center gap-4">
  <img src="/fiscobem.svg" alt="fiscobem" className="h-7 w-auto flex-shrink-0" />
  <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/login')}
              className="px-5 py-2 rounded-lg text-sm font-semibold text-primary border border-primary/20 hover:bg-primary/5 transition-colors"
            >
              Entrar
            </button>
            <button
              onClick={() => navigate('/register')}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold bg-gradient-primary text-white hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              Registar
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </header>

      {/* ── HERO ───────────────────────────────────────────────────── */}
      <section className="relative pt-36 pb-28 px-6 overflow-hidden">
        {/* Ambient blobs */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-24 right-1/4 w-[500px] h-[500px] bg-secondary/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-purple-500/4 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-4xl mx-auto text-center relative">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-primary/15 shadow-sm mb-8">
            <Zap size={14} className="text-primary" />
            <span className="text-sm font-medium text-gray-700">Para Profissionais Independentes</span>
          </div>

          {/* Headline */}
          <h1 className="text-5xl md:text-6xl lg:text-[4.25rem] font-extrabold leading-[1.1] mb-6 text-gray-900 tracking-tight">
            A plataforma de aquisição de clientes para{' '}
            <span className="text-transparent bg-clip-text bg-gradient-primary">
              Contabilistas
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg md:text-xl text-gray-900/55 mb-10 max-w-3xl mx-auto leading-relaxed">
            Receba pedidos de contacto qualificados diretamente no seu dashboard e faça crescer o seu negócio de contabilidade de forma independente.
          </p>

          {/* Benefit pills */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {heroChips.map((chip, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-gray-100"
              >
                <CheckCircle2 size={14} className="text-primary flex-shrink-0" />
                <span className="text-sm font-medium text-gray-700">{chip}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="flex gap-4 justify-center flex-wrap">
            <button
              onClick={() => navigate('/register')}
              className="flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-lg bg-gradient-primary text-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-200"
            >
              Começar Agora
              <ArrowRight size={18} />
            </button>
          </div>

          {/* Trust line */}
          <div className="mt-10 flex items-center justify-center gap-2 text-sm text-gray-400">
            <Shield size={13} className="text-primary" />
            <span>✓ Sem taxas de configuração &nbsp;·&nbsp; ✓ Sem subscrições &nbsp;·&nbsp; ✓ Cancela quando quiseres</span>
          </div>
        </div>
      </section>

      {/* ── PRICING ────────────────────────────────────────────────── */}
      <section className="px-6 py-24 bg-background">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-gray-900 tracking-tight">
              Preço simples e transparente
            </h2>
            <p className="text-lg text-gray-500">
              Pague apenas pelos pedidos de contacto que recebe
            </p>
          </div>

          <div className="max-w-md mx-auto bg-gradient-primary rounded-2xl p-12 text-center shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all">
            <div className="text-white/70 text-sm font-semibold uppercase tracking-wider mb-3">
              Por lead
            </div>
            <div className="text-7xl font-extrabold text-white mb-4">
              5€
            </div>
            <div className="h-px bg-white/30 max-w-[200px] mx-auto mb-6"></div>
            <ul className="text-white/80 text-sm space-y-2">
              <li>✓ Sem subscrição mensal</li>
              <li>✓ Sem custo de adesão</li>
              <li>✓ Reembolso em leads irrelevantes</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ───────────────────────────────────────────────── */}
      <section className="px-6 py-24 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-gray-900 tracking-tight">
              Porquê escolher a fiscobem.pt
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              Tudo o que precisa para potenciar o seu negócio de forma independente
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <div
                key={i}
                className="p-8 rounded-2xl border border-gray-100 bg-background shadow-sm hover:-translate-y-2 hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${b.bg} ${b.color}`}>
                  {b.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{b.title}</h3>
                <p className="text-gray-500 leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIAL / CEO QUOTE ────────────────────────────────── */}
      <section className="px-6 py-24 bg-gradient-primary relative overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-12 items-center">
            {/* CEO Image */}
            <div className="relative flex justify-center">
              <div className="absolute w-full h-full bg-gradient-radial from-secondary/15 via-purple-500/15 to-transparent blur-3xl" />
              <img
                src="/team-image.jpg"
                alt="CEO"
                className="relative max-w-sm w-full h-auto rounded-3xl shadow-2xl border-4 border-secondary/20 object-cover"
                style={{ aspectRatio: '3/4', maxHeight: '500px' }}
                onError={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, rgba(43,181,255,0.3), rgba(138,101,255,0.3))';
                  e.target.style.minHeight = '400px';
                }}
              />
            </div>

            {/* Quote */}
            <div className="flex flex-col gap-10 p-8">
              <p className="text-2xl leading-relaxed text-white italic font-medium relative pl-8">
                <span className="absolute -left-1 -top-2 text-8xl text-secondary/20 font-serif leading-none">"</span>
                Muitos profissionais qualificados trabalham para escritórios não por necessidade, mas pela segurança e carteira de clientes que estas estruturas oferecem - frequentemente em troca de uma baixa remuneração. Criámos a fiscobem.pt para eliminar estas barreiras.
              </p>
              <div className="flex flex-col gap-2 pt-6 border-t-2 border-secondary/20">
                <div className="text-3xl font-bold text-white">
                  Equipa fiscobem.pt
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────────────── */}
      <section className="px-6 py-24 bg-background text-center border-t border-primary/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">
            Pronto para começar a receber leads?
          </h2>
          <div className="flex gap-4 justify-center flex-wrap mb-6">
            <button
              onClick={() => navigate('/register')}
              className="flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-lg bg-gradient-primary text-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-200"
            >
              Começar Agora
              <ArrowRight size={18} />
            </button>
          </div>
          <p className="text-gray-900/70 text-sm">
            ✓ Sem taxas de configuração &nbsp;·&nbsp; ✓ Sem subscrições &nbsp;·&nbsp; ✓ Cancela quando quiseres
          </p>
        </div>
      </section>

      {/* ── FOOTER ─────────────────────────────────────────────────── */}
      <footer className="bg-gradient-surface px-6 py-12 text-center">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-6">
          <img src="/fiscobem.svg" alt="fiscobem" className="h-12" />
          <p className="text-sm text-gray-600">
            &copy; 2025 fiscobem.pt. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;